"use client";

import type { JSX } from "react";
import { SubscribeForm } from "@/components/SubscribeForm";
import { Zap, TrendingUp, type LucideIcon } from "lucide-react";
import { Container } from "./ui/Container";

interface BenefitItem {
  icon: LucideIcon;
  title: string;
  description: string;
}

const benefits: BenefitItem[] = [
  {
    icon: Zap,
    title: "Weekly Insights",
    description:
      "Get actionable fitness tips and nutrition strategies delivered every week, no fluff, just results.",
  },
  {
    icon: TrendingUp,
    title: "Track Your Progress",
    description:
      "Exclusive tools and calculators to monitor your gains and optimize your performance.",
  },
];

interface NewsletterSectionProps {
  className?: string;
}

export function NewsletterSection({ className = "" }: NewsletterSectionProps): JSX.Element {
  return (
    <div className={`relative isolate overflow-hidden py-16 sm:py-24 lg:py-32 ${className}`}>
      {/* Content container */}
      <Container size="lg" padding="md">
        <div className="mx-auto grid max-w-2xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-2">
          {/* Left column - Form */}
          <div className="max-w-xl lg:max-w-lg">
            {/* Badge */}
            <div className="mb-4">
              <span className="inline-flex items-center rounded-full bg-indigo-100 px-3 py-1 text-sm font-medium text-indigo-700">
                Weekly newsletter
              </span>
            </div>

            {/* Headline */}
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Training, nutrition, and lifestyle tips: one email per week
            </h2>

            {/* Description */}
            <p className="mt-4 text-lg leading-8 text-gray-600">
              Each issue brings you practical fitness strategies, nutrition
              insights, and lifestyle guidance you can apply right away. No
              fluff. Just useful, expert-led reading in your inbox.
            </p>

            {/* Form */}
            <div className="mt-6">
              <SubscribeForm
                source="homepage-newsletter-section"
                placeholder="Enter your email"
                buttonText="Subscribe"
              />
            </div>

            {/* Trust signal */}
            <p className="mt-4 text-sm text-gray-500">
              One email per week. Unsubscribe anytime.
            </p>
          </div>

          {/* Right column - Benefits */}
          <dl className="grid grid-cols-1 gap-x-8 gap-y-10 sm:grid-cols-2 lg:pt-2">
            {benefits.map((benefit) => {
              const IconComponent = benefit.icon;
              return (
                <div key={benefit.title} className="flex flex-col items-start">
                  <dt className="flex flex-col items-start gap-y-3">
                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-indigo-50 shadow-sm ring-1 ring-indigo-600/10 transition-colors duration-200 hover:bg-indigo-100">
                      <IconComponent
                        className="text-indigo-600"
                        size={24}
                        strokeWidth={2}
                        aria-hidden="true"
                      />
                    </div>
                    <span className="text-base font-semibold leading-7 text-gray-900">
                      {benefit.title}
                    </span>
                  </dt>
                  <dd className="mt-2 text-sm leading-6 text-gray-600">
                    {benefit.description}
                  </dd>
                </div>
              );
            })}
          </dl>
        </div>
      </Container>
    </div>
  );
}
